// src/ApiPanel/components/reports/UploadReport.jsx
// The engine behind Upload GST, Upload GST Recharge and Upload Cash
// Declaration. All three are the same flow with a different endpoint and
// column set, so they share one component.
//
// THE FLOW, AND WHY IT HAS FOUR STEPS INSTEAD OF ONE
// --------------------------------------------------
//   pick file -> upload (server parses + validates every row, writes nothing)
//             -> review the staged rows and their errors
//             -> someone ELSE commits, and only then is anything written
//
// A CSV that lands straight in the invoice register is a financial write with
// no review: it could book input credit that does not exist, or file cash
// declarations for retailers who never made them. So the upload stages, and
// a second person commits.
//
// The client-side checks here (extension, size, empty file) exist to save a
// round trip, not to enforce anything. Every one of them is repeated
// server-side, where it counts — a client check is a hint to the user, never
// a control.
import React, { useCallback, useRef, useState } from "react";
import {
  Badge, Card, DataTable, EmptyState, Modal, Notice, Pagination, ReportPage,
  count, prettyDate,
} from "./ReportKit";
import { useAction } from "../../hooks/useReportQuery";
import { apiPanelDownload, apiPanelFetch, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

const MAX_BYTES = 5 * 1024 * 1024;

export default function UploadReport({
  title, subtitle, uploadType, endpoint, columns, guidance,
}) {
  const { user } = useApiPanelSession();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [clientError, setClientError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [batches, setBatches] = useState([]);
  const [batchMeta, setBatchMeta] = useState(null);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [batchesError, setBatchesError] = useState("");
  const [detail, setDetail] = useState(null);

  const action = useAction();

  const loadBatches = useCallback(
    async (page = 1) => {
      setBatchesLoading(true);
      setBatchesError("");
      try {
        const res = await apiPanelFetch(
          `${API_PANEL_ENDPOINTS.CA_UPLOAD_BATCH_LIST}${qs({ upload_type: uploadType, page })}`
        );
        setBatches(res?.data || []);
        setBatchMeta(res?.pagination || null);
      } catch (err) {
        setBatchesError(err.message || "Could not load previous uploads.");
      } finally {
        setBatchesLoading(false);
      }
    },
    [uploadType]
  );

  React.useEffect(() => {
    loadBatches(1);
  }, [loadBatches]);

  const pickFile = (chosen) => {
    setClientError("");
    setResult(null);
    if (!chosen) {
      setFile(null);
      return;
    }
    if (!chosen.name.toLowerCase().endsWith(".csv")) {
      setClientError("Only .csv files are accepted.");
      setFile(null);
      return;
    }
    if (chosen.size === 0) {
      setClientError("That file is empty.");
      setFile(null);
      return;
    }
    if (chosen.size > MAX_BYTES) {
      setClientError(`That file is ${(chosen.size / 1024 / 1024).toFixed(1)} MB — the limit is 5 MB.`);
      setFile(null);
      return;
    }
    setFile(chosen);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setClientError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await apiPanelFetch(endpoint, { method: "POST", body });
      setResult(res);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadBatches(1);
    } catch (err) {
      setClientError(err.message || "The upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () =>
    apiPanelDownload(
      `${API_PANEL_ENDPOINTS.CA_UPLOAD_TEMPLATE}${qs({ upload_type: uploadType })}`,
      `${uploadType.toLowerCase()}_template.csv`
    ).catch(() => setClientError("Could not download the template."));

  const openBatch = async (batch) => {
    setDetail({ loading: true, batch });
    try {
      const res = await apiPanelFetch(
        `${API_PANEL_ENDPOINTS.CA_UPLOAD_BATCH_DETAIL}${qs({ batch_id: batch.batch_id, only: "invalid" })}`
      );
      setDetail({ loading: false, batch, ...res });
    } catch (err) {
      setDetail(null);
      setBatchesError(err.message || "Could not open that batch.");
    }
  };

  const commit = async (batch) => {
    if (!window.confirm(
      `Commit ${count(batch.valid_rows)} row(s) from "${batch.original_filename}"? ` +
      `This writes them to the live register and cannot be undone from here.`
    )) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.CA_UPLOAD_BATCH_COMMIT,
      { batch_id: batch.batch_id },
      { form: true }
    );
    if (res) {
      setDetail(null);
      loadBatches(1);
    }
  };

  const reject = async (batch) => {
    const reason = window.prompt("Why is this batch being rejected?");
    if (!reason || reason.trim().length < 3) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.CA_UPLOAD_BATCH_REJECT,
      { batch_id: batch.batch_id, reason: reason.trim() },
      { form: true }
    );
    if (res) {
      setDetail(null);
      loadBatches(1);
    }
  };

  return (
    <ReportPage
      title={title}
      subtitle={subtitle}
      actions={
        <button type="button" className="apip-btn apip-btn-ghost" onClick={downloadTemplate}>
          <i className="iconoir-download" /> Download template
        </button>
      }
    >
      <Notice tone="info">
        Uploading only <strong>stages and checks</strong> your file — nothing is written yet.
        Someone other than you then reviews and commits it. A file with any invalid row cannot be
        committed at all; fix the rows and upload again.
      </Notice>

      {guidance}

      <Card title="Upload a file">
        <form onSubmit={upload}>
          <div
            className={`apip-dropzone${dragging ? " dragging" : ""}${file ? " has-file" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => fileRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
          >
            <i className="iconoir-cloud-upload" />
            {file ? (
              <>
                <strong>{file.name}</strong>
                <small>{(file.size / 1024).toFixed(1)} KB · ready to upload</small>
              </>
            ) : (
              <>
                <strong>Drop a CSV here, or click to choose</strong>
                <small>.csv only · up to 5 MB · use the template so the columns match</small>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
          </div>

          {clientError && <Notice tone="bad">{clientError}</Notice>}

          <div className="apip-form-actions">
            <button type="submit" className="apip-btn apip-btn-primary" disabled={!file || uploading}>
              {uploading ? "Uploading…" : "Upload & Validate"}
            </button>
            {file && (
              <button
                type="button"
                className="apip-btn apip-btn-ghost"
                onClick={() => { pickFile(null); if (fileRef.current) fileRef.current.value = ""; }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {result && <UploadResult result={result} />}
      </Card>

      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      <Card title="Previous uploads">
        <DataTable
          columns={[
            {
              key: "original_filename",
              label: "File",
              render: (b) => (
                <span className="apip-party">
                  {b.original_filename}
                  <small>{(b.byte_size / 1024).toFixed(1)} KB</small>
                </span>
              ),
            },
            { key: "uploaded_at", label: "Uploaded", render: (b) => b.uploaded_at },
            { key: "uploaded_by", label: "By" },
            { key: "row_count", label: "Rows", numeric: true, render: (b) => count(b.row_count) },
            {
              key: "valid_rows",
              label: "Valid",
              numeric: true,
              render: (b) => <span className="apip-ok-txt">{count(b.valid_rows)}</span>,
            },
            {
              key: "invalid_rows",
              label: "Invalid",
              numeric: true,
              render: (b) =>
                b.invalid_rows > 0 ? (
                  <span className="apip-bad-txt">{count(b.invalid_rows)}</span>
                ) : (
                  "0"
                ),
            },
            { key: "status", label: "Status", render: (b) => <Badge value={b.status} /> },
            {
              key: "actions",
              label: "",
              width: 210,
              render: (b) => {
                const mine = b.uploaded_by && user?.name && b.uploaded_by === user.name;
                return (
                  <div className="apip-token-actions">
                    <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost" onClick={() => openBatch(b)}>
                      View
                    </button>
                    {b.status === "VALIDATED" && !mine && (
                      <button type="button" className="apip-btn apip-btn-sm apip-btn-primary" disabled={action.busy} onClick={() => commit(b)}>
                        Commit
                      </button>
                    )}
                    {b.status === "VALIDATED" && mine && (
                      <span className="apip-hint">Needs another approver</span>
                    )}
                    {(b.status === "VALIDATED" || b.status === "UPLOADED") && (
                      <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost apip-btn-danger" disabled={action.busy} onClick={() => reject(b)}>
                        Reject
                      </button>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={batches}
          loading={batchesLoading}
          error={batchesError}
          empty="Nothing uploaded for this report yet."
          rowKey={(b) => b.batch_id}
        />
        <Pagination meta={batchMeta} onPage={loadBatches} />
      </Card>

      <Modal
        open={!!detail}
        wide
        title={detail?.batch ? `Rows needing attention — ${detail.batch.original_filename}` : "Batch"}
        onClose={() => setDetail(null)}
      >
        {detail?.loading && <div className="apip-loading">Loading…</div>}
        {detail && !detail.loading && (
          <>
            <dl className="apip-kv apip-kv-2">
              <dt>Status</dt><dd><Badge value={detail.batch.status} /></dd>
              <dt>Rows</dt><dd>{count(detail.batch.row_count)}</dd>
              <dt>Valid</dt><dd>{count(detail.batch.valid_rows)}</dd>
              <dt>Invalid</dt><dd>{count(detail.batch.invalid_rows)}</dd>
            </dl>

            {detail.batch.error_summary?.length > 0 && (
              <>
                <h4 className="apip-form-subhead">Most common problems</h4>
                <ul className="apip-error-summary">
                  {detail.batch.error_summary.map((e, i) => (
                    <li key={i}>
                      <span className="apip-badge apip-badge-bad">{e.rows}</span> {e.error}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {detail.data?.length ? (
              <>
                <h4 className="apip-form-subhead">Invalid rows</h4>
                <DataTable
                  columns={[
                    { key: "row_no", label: "Row", width: 60 },
                    ...columns.map((c) => ({
                      key: c.key,
                      label: c.label,
                      render: (r) => r.raw?.[c.key] ?? "—",
                    })),
                    {
                      key: "errors",
                      label: "Problems",
                      render: (r) => (
                        <ul className="apip-row-errors">
                          {(r.errors || []).map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                        </ul>
                      ),
                    },
                  ]}
                  rows={detail.data}
                  rowKey={(r) => r.row_no}
                />
              </>
            ) : (
              <EmptyState icon="iconoir-check-circle" message="Every row in this file passed validation." />
            )}
          </>
        )}
      </Modal>
    </ReportPage>
  );
}

function UploadResult({ result }) {
  const clean = result.batch_status === "VALIDATED";
  return (
    <div className={`apip-upload-result ${clean ? "ok" : "bad"}`}>
      <div className="apip-upload-result-head">
        <i className={clean ? "iconoir-check-circle" : "iconoir-warning-triangle"} />
        <div>
          <strong>{result.message}</strong>
          <small>
            {count(result.row_count)} rows · {count(result.valid_rows)} valid ·{" "}
            {count(result.invalid_rows)} invalid
          </small>
        </div>
      </div>
      {result.error_summary?.length > 0 && (
        <ul className="apip-error-summary">
          {result.error_summary.map((e, i) => (
            <li key={i}>
              <span className="apip-badge apip-badge-bad">{e.rows}</span> {e.error}
            </li>
          ))}
        </ul>
      )}
      {clean && (
        <p className="apip-hint">
          Waiting for approval. Someone other than you has to commit it from the list below.
        </p>
      )}
    </div>
  );
}
